import { FolderType, SourceFilter } from './helpers';
import { SortKey } from './mediaCardUtils';

export type LibraryViewMode = 'all' | 'grouped' | 'favourites' | 'uploads';
export type MediaPageSize = 20 | 50 | 100;

export interface MediaLibraryQueryState {
  page: number;
  pageSize: MediaPageSize;
  search: string;
  folderType: FolderType;
  sourceFilter: SourceFilter;
  sortKey: SortKey;
  viewMode: LibraryViewMode;
  hasControlnet: boolean;
  recentDays: number | null;
  activePresetId: string | null;
}

export interface MediaFilterPresetValues {
  search: string;
  folderType: FolderType;
  sourceFilter: SourceFilter;
  sortKey: SortKey;
  viewMode: LibraryViewMode;
  hasControlnet: boolean;
  recentDays: number | null;
}

export interface MediaFilterPreset {
  id: string;
  name: string;
  builtIn?: boolean;
  values: MediaFilterPresetValues;
}

export const MEDIA_PAGE_SIZES: MediaPageSize[] = [20, 50, 100];

export const DEFAULT_MEDIA_LIBRARY_QUERY: MediaLibraryQueryState = {
  page: 1,
  pageSize: 20,
  search: '',
  folderType: 'all',
  sourceFilter: 'all',
  sortKey: 'newest',
  viewMode: 'all',
  hasControlnet: false,
  recentDays: null,
  activePresetId: null,
};

export const BUILT_IN_MEDIA_PRESETS: MediaFilterPreset[] = [
  {
    id: 'generated-controlnet',
    name: 'Generated ControlNet images',
    builtIn: true,
    values: {
      search: '',
      folderType: 'image',
      sourceFilter: 'generated',
      sortKey: 'newest',
      viewMode: 'all',
      hasControlnet: true,
      recentDays: null,
    },
  },
  {
    id: 'last-7-days',
    name: 'Last 7 days',
    builtIn: true,
    values: {
      search: '',
      folderType: 'all',
      sourceFilter: 'all',
      sortKey: 'newest',
      viewMode: 'all',
      hasControlnet: false,
      recentDays: 7,
    },
  },
];

const PRESET_STORAGE_KEY = 'contentgen.media.filter-presets.v1';

const isFolderType = (value: unknown): value is FolderType =>
  value === 'all' || value === 'image' || value === 'video' || value === 'audio';
const isSourceFilter = (value: unknown): value is SourceFilter =>
  value === 'all' || value === 'uploaded' || value === 'generated';
const isSortKey = (value: unknown): value is SortKey =>
  value === 'newest' || value === 'oldest' || value === 'favourite' || value === 'name';
const isViewMode = (value: unknown): value is LibraryViewMode =>
  value === 'all' || value === 'grouped' || value === 'favourites' || value === 'uploads';

const parsePositiveInt = (value: string | null, fallback: number): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const readMediaLibraryUrlState = (search: string): MediaLibraryQueryState => {
  const params = new URLSearchParams(search);
  const rawPageSize = parsePositiveInt(params.get('limit'), 20);
  const rawRecentDays = params.get('days');
  const recentDays = rawRecentDays
    ? Math.min(365, parsePositiveInt(rawRecentDays, 7))
    : null;
  const folderType = params.get('type');
  const sourceFilter = params.get('source');
  const sortKey = params.get('sort');
  const viewMode = params.get('view');

  return {
    page: parsePositiveInt(params.get('page'), 1),
    pageSize: MEDIA_PAGE_SIZES.includes(rawPageSize as MediaPageSize)
      ? (rawPageSize as MediaPageSize)
      : 20,
    search: params.get('q')?.trim() || '',
    folderType: isFolderType(folderType) ? folderType : 'all',
    sourceFilter: isSourceFilter(sourceFilter) ? sourceFilter : 'all',
    sortKey: isSortKey(sortKey) ? sortKey : 'newest',
    viewMode: isViewMode(viewMode) ? viewMode : 'all',
    hasControlnet: params.get('controlnet') === '1',
    recentDays,
    activePresetId: params.get('preset')?.trim() || null,
  };
};

export const buildMediaLibraryUrlSearch = (state: MediaLibraryQueryState): string => {
  const params = new URLSearchParams();
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== 20) params.set('limit', String(state.pageSize));
  if (state.search.trim()) params.set('q', state.search.trim());
  if (state.folderType !== 'all') params.set('type', state.folderType);
  if (state.sourceFilter !== 'all') params.set('source', state.sourceFilter);
  if (state.sortKey !== 'newest') params.set('sort', state.sortKey);
  if (state.viewMode !== 'all') params.set('view', state.viewMode);
  if (state.hasControlnet) params.set('controlnet', '1');
  if (state.recentDays) params.set('days', String(state.recentDays));
  if (state.activePresetId) params.set('preset', state.activePresetId);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

export const createdAfterForRecentDays = (
  recentDays: number | null,
  now = new Date()
): string | undefined => {
  if (!recentDays) return undefined;
  const cutoff = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  cutoff.setUTCDate(cutoff.getUTCDate() - (recentDays - 1));
  return cutoff.toISOString();
};

const isPresetValues = (value: unknown): value is MediaFilterPresetValues => {
  if (!value || typeof value !== 'object') return false;
  const values = value as Partial<MediaFilterPresetValues>;
  return (
    typeof values.search === 'string' &&
    isFolderType(values.folderType) &&
    isSourceFilter(values.sourceFilter) &&
    isSortKey(values.sortKey) &&
    isViewMode(values.viewMode) &&
    typeof values.hasControlnet === 'boolean' &&
    (values.recentDays === null ||
      (typeof values.recentDays === 'number' && Number.isInteger(values.recentDays) && values.recentDays > 0))
  );
};

export const readSavedMediaPresets = (): MediaFilterPreset[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(PRESET_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is MediaFilterPreset => {
      if (!entry || typeof entry !== 'object') return false;
      const preset = entry as Partial<MediaFilterPreset>;
      return (
        typeof preset.id === 'string' &&
        typeof preset.name === 'string' &&
        preset.name.trim().length > 0 &&
        isPresetValues(preset.values)
      );
    });
  } catch {
    return [];
  }
};

export const persistSavedMediaPresets = (presets: MediaFilterPreset[]): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(presets));
};

export const makePresetId = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'filter';
  return `custom-${slug}-${Date.now().toString(36)}`;
};
